/**
 * Der Abschnitt im Original-PDF der Richtlinie — mit pdf.js wie im Dokumentbetrachter, auf der Seite
 * des Abschnitts geöffnet. Erkannte Anforderungen und Belegstellen der Kanten liegen als Markierung
 * (CSS Custom Highlight API) über dem Textlayer seiner Seiten; ein Klick auf eine Belegstelle wählt
 * die Kante. Entwürfe gibt es nur in der Textansicht — das PDF ist die geltende Fassung.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "pdfjs-dist/legacy/web/pdf_viewer.css";

import { loadPdfRuntime, SAFE_DOCUMENT_OPTIONS } from "../documentViewer/pdfRuntime";
import { releasePdfDocument, whenPdfWorkerIdle } from "../documentViewer/pdfTeardown";
import { useGuidelinesAPI, type RequirementKind } from "./GuidelinesAPI";
import { findPhraseRanges } from "./pdfMatch";
import { paintSearchTerms } from "./textMarks";

export interface PdfMark {
  text: string;
  kind: RequirementKind | "link";
  linkId?: string;
}

const HIGHLIGHTS = ["gl-muss", "gl-soll", "gl-kann", "gl-link", "gl-link-current"] as const;

/** Bytes je Dokument — der Wechsel zwischen Abschnitten lädt das PDF nicht neu. */
const pdfCache = new Map<string, Promise<ArrayBuffer>>();

const highlightApi = (): { highlights: Map<string, unknown>; Highlight: new (...ranges: Range[]) => unknown } | null => {
  const css: any = typeof CSS !== "undefined" ? CSS : null;
  const Highlight = (globalThis as any).Highlight;
  return css?.highlights && typeof Highlight === "function" ? { highlights: css.highlights, Highlight } : null;
};

const clearHighlights = () => {
  const api = highlightApi();
  for (const name of HIGHLIGHTS) api?.highlights.delete(name);
};

export function GuidelinePdfView(props: {
  /** Begriffe der Volltextsuche, gefaltet (search.ts). */
  searchTerms?: string[];
  customer: string;
  documentId: string;
  pageFrom: number;
  pageTo: number;
  /** Überschrift des Abschnitts („4.7.1 Allgemeines") — dorthin springt die Ansicht. */
  heading: string;
  marks: PdfMark[];
  selectedLink: string | null;
  onSelectLink: (id: string) => void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const pdfViewerRef = useRef<any>(null);
  const linkRangesRef = useRef<Array<{ range: Range; linkId: string }>>([]);
  // Für welche Überschrift schon gesprungen wurde — danach blättert der Leser selbst.
  const scrolledRef = useRef<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  // Hochgezählt, sobald pdf.js den Textlayer einer Seite des Abschnitts gezeichnet hat.
  const [layerTick, setLayerTick] = useState(0);
  const pagesRef = useRef({ from: props.pageFrom, to: props.pageTo });
  pagesRef.current = { from: props.pageFrom, to: props.pageTo };

  // 1. Laden und zeichnen — einmal je Dokument.
  useEffect(() => {
    let cancelled = false;
    let loadingTask: any = null;
    let pdfViewer: any = null;
    setStatus("loading");

    (async () => {
      if (!pdfCache.has(props.documentId)) {
        const pending = api.getPdf(props.documentId);
        pending.catch(() => pdfCache.delete(props.documentId));
        pdfCache.set(props.documentId, pending);
      }
      const data = await pdfCache.get(props.documentId)!;
      const { pdfjs, viewer } = await loadPdfRuntime();
      if (cancelled || !containerRef.current || !viewerRef.current) return;
      const eventBus = new viewer.EventBus();
      const linkService = new viewer.PDFLinkService({ eventBus });
      pdfViewer = new viewer.PDFViewer({ container: containerRef.current, viewer: viewerRef.current, eventBus, linkService });
      pdfViewerRef.current = pdfViewer;
      linkService.setViewer(pdfViewer);
      await whenPdfWorkerIdle();
      if (cancelled) return;
      loadingTask = pdfjs.getDocument({ data: data.slice(0), ...SAFE_DOCUMENT_OPTIONS });
      const loaded = await loadingTask.promise;
      if (cancelled) return;
      pdfViewer.setDocument(loaded);
      linkService.setDocument(loaded);
      eventBus.on("pagesinit", () => {
        pdfViewer.currentScaleValue = "page-width";
        pdfViewer.currentPageNumber = pagesRef.current.from;
      });
      eventBus.on("pagesloaded", () => {
        // Erst jetzt tragen die Seiten ihr echtes Maß (Querformat) — neu einpassen und hinspringen.
        pdfViewer.currentPageNumber = pagesRef.current.from;
        pdfViewer.currentScaleValue = "page-width";
        pdfViewer.scrollPageIntoView({ pageNumber: pagesRef.current.from });
      });
      eventBus.on("textlayerrendered", (event: any) => {
        const { from, to } = pagesRef.current;
        if (event.pageNumber >= from && event.pageNumber <= to) setLayerTick((n) => n + 1);
      });
      setStatus("ready");
    })().catch(() => !cancelled && setStatus("error"));

    return () => {
      cancelled = true;
      pdfViewerRef.current = null;
      clearHighlights();
      try {
        pdfViewer?.setDocument(null);
      } catch {
        /* schon abgebaut */
      }
      releasePdfDocument(loadingTask);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customer, props.documentId]);

  // 2. Anderer Abschnitt: auf seine erste Seite springen.
  useEffect(() => {
    const viewer = pdfViewerRef.current;
    if (status !== "ready" || !viewer?.pagesCount) return;
    viewer.currentPageNumber = props.pageFrom;
    // „Seitenbreite" misst an der aktuellen Seite — eine Querformatseite passt sonst nicht.
    viewer.currentScaleValue = "page-width";
    viewer.scrollPageIntoView({ pageNumber: props.pageFrom });
  }, [status, props.pageFrom]);

  // 3. Markieren — über den Textlayern der Seiten des Abschnitts, die schon gezeichnet sind.
  useEffect(() => {
    const api = highlightApi();
    const viewer = viewerRef.current;
    if (!api || !viewer || status !== "ready") return;
    const layers = Array.from(viewer.querySelectorAll<HTMLElement>(".page"))
      .filter((page) => {
        const n = Number(page.dataset.pageNumber);
        return n >= props.pageFrom && n <= props.pageTo;
      })
      .map((page) => page.querySelector(".textLayer"))
      .filter((layer): layer is Element => !!layer);
    const byName = new Map<string, Range[]>(HIGHLIGHTS.map((name) => [name, []]));
    const links: Array<{ range: Range; linkId: string }> = [];
    for (const layer of layers) {
      findPhraseRanges(layer, props.marks.map((m) => m.text)).forEach((range, i) => {
        if (!range) return;
        const mark = props.marks[i];
        if (mark.kind === "link" && mark.linkId) {
          links.push({ range, linkId: mark.linkId });
          byName.get(mark.linkId === props.selectedLink ? "gl-link-current" : "gl-link")!.push(range);
        } else byName.get(`gl-${mark.kind}`)?.push(range);
      });
    }
    linkRangesRef.current = links;
    if (scrolledRef.current !== props.heading && containerRef.current) {
      const [head] = layers.map((layer) => findPhraseRanges(layer, [props.heading], 40)[0]).filter(Boolean);
      if (head) {
        const box = head.getBoundingClientRect();
        const frame = containerRef.current.getBoundingClientRect();
        containerRef.current.scrollTop += box.top - frame.top - 24;
        scrolledRef.current = props.heading;
      }
    }
    for (const [name, ranges] of Array.from(byName.entries())) {
      if (ranges.length) api.highlights.set(name, new api.Highlight(...ranges));
      else api.highlights.delete(name);
    }
    paintSearchTerms(layers, props.searchTerms ?? []);
  }, [status, layerTick, props.marks, props.pageFrom, props.pageTo, props.selectedLink, props.heading, props.searchTerms]);

  // Ein Klick auf eine Belegstelle wählt ihre Kante.
  const onClick = (event: React.MouseEvent) => {
    const doc: any = document;
    const caret = doc.caretRangeFromPoint?.(event.clientX, event.clientY);
    const pos = caret ? { node: caret.startContainer, offset: caret.startOffset } : doc.caretPositionFromPoint?.(event.clientX, event.clientY);
    const node = pos?.node ?? pos?.offsetNode;
    if (!node) return;
    const hit = linkRangesRef.current.find(({ range }) => {
      try {
        return range.isPointInRange(node, pos.offset);
      } catch {
        return false;
      }
    });
    if (hit) props.onSelectLink(hit.linkId);
  };

  return (
    <div className="relative h-full min-h-0 flex-1 bg-muted/40">
      {/* pdf.js verlangt einen absolut positionierten Scroll-Container. */}
      <div ref={containerRef} className="guideline-pdf absolute inset-0 overflow-auto" onClick={onClick}>
        <div ref={viewerRef} className="pdfViewer" />
      </div>
      {status !== "ready" ? (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {status === "loading" ? t("guidelinesPage.pdfLoading") : t("guidelinesPage.pdfError")}
        </div>
      ) : null}
    </div>
  );
}
